import { useState, useEffect } from 'react';
import { Crosshair, Trophy, Users } from 'lucide-react';
import { Event } from '../../services/events';
import {
  PredictionGame,
  PredictionTieBreak,
  createPredictionGame,
  getGameHistory,
} from '../../services/prediction';
import type { PrizeType } from '../../services/prizes';
import toast from 'react-hot-toast';

interface PredictionPanelProps {
  eventId: string;
  djId: string;
  event: Event;
  setEvent: React.Dispatch<React.SetStateAction<Event | null>>;
  onCreated?: () => void;
}

const PRIZE_OPTIONS: { type: PrizeType; label: string; icon: string }[] = [
  { type: 'pick_next_song', label: 'Pick Next Song', icon: '🎵' },
  { type: 'auto_queue_bypass', label: 'Queue Bypass', icon: '⚡' },
  { type: 'shoutout', label: 'Shoutout', icon: '📣' },
  { type: 'custom', label: 'Custom', icon: '🎁' },
];

export default function PredictionPanel({ eventId, djId, event, setEvent, onCreated }: PredictionPanelProps) {
  const [history, setHistory] = useState<PredictionGame[]>([]);
  const [creating, setCreating] = useState(false);

  const [songDelay, setSongDelay] = useState(2);
  const [countdownVisible, setCountdownVisible] = useState(true);
  const [tieBreakMethod, setTieBreakMethod] = useState<PredictionTieBreak>('speed');
  const [prizeType, setPrizeType] = useState<PrizeType>('shoutout');
  const [customText, setCustomText] = useState('');

  useEffect(() => {
    getGameHistory(eventId).then(setHistory).catch(() => {});
  }, [eventId]);

  const handleCreate = async () => {
    if (creating) return;
    setCreating(true);
    try {
      await createPredictionGame(eventId, djId, {
        songDelay,
        countdownVisible,
        tieBreakMethod,
        prizeType,
        prizeCustomText: customText || undefined,
        previousHideRankingInfo: event.settings.hideRankingInfo || false,
      });
      const updatedEvent = { ...event, settings: { ...event.settings, hideRankingInfo: true, predictionGameActive: true } };
      setEvent(updatedEvent);
      toast.success('Prediction game created! Lock an answer song in the queue banner.');
      onCreated?.();
    } catch (error: any) {
      console.error('Failed to create prediction game:', error);
      toast.error(error.message || 'Failed to create prediction game');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        <div>
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
            Song Delay
          </label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={0}
              max={10}
              value={songDelay}
              onChange={(e) => setSongDelay(Math.min(10, Math.max(0, parseInt(e.target.value) || 0)))}
              className="w-20 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
            />
            <span className="text-sm text-gray-500 dark:text-gray-400">songs before game starts</span>
          </div>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Set to 0 for immediate start</p>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Countdown visible</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Attendees see "game in X songs"</p>
          </div>
          <button
            type="button"
            onClick={() => setCountdownVisible(!countdownVisible)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              countdownVisible ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                countdownVisible ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">Tie-break Method</label>
          <div className="flex gap-2">
            {(['speed', 'random', 'shared'] as PredictionTieBreak[]).map((method) => (
              <button
                key={method}
                onClick={() => setTieBreakMethod(method)}
                className={`flex-1 px-3 py-2 text-sm rounded-lg border capitalize ${
                  tieBreakMethod === method
                    ? 'border-primary bg-primary/10 text-primary font-medium'
                    : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
              >
                {method === 'shared' ? 'All Win' : method === 'speed' ? 'Fastest' : 'Random'}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">Prize</label>
          <div className="grid grid-cols-2 gap-2">
            {PRIZE_OPTIONS.map((opt) => (
              <button
                key={opt.type}
                onClick={() => setPrizeType(opt.type)}
                className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg border ${
                  prizeType === opt.type
                    ? 'border-primary bg-primary/10 text-primary font-medium'
                    : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
              >
                <span>{opt.icon}</span>
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {(prizeType === 'custom' || prizeType === 'shoutout') && (
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
              {prizeType === 'shoutout' ? 'Shoutout message' : 'Prize description'}
            </label>
            <input
              type="text"
              value={customText}
              onChange={(e) => setCustomText(e.target.value)}
              placeholder={prizeType === 'shoutout' ? 'e.g., "Congrats to our winner!"' : 'e.g., "Free drink at the bar"'}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
            />
          </div>
        )}
      </div>

      <button
        onClick={handleCreate}
        disabled={creating}
        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary/90 font-medium disabled:opacity-50"
      >
        <Crosshair className="w-4 h-4" />
        {creating ? 'Creating...' : 'Create Prediction Game'}
      </button>

      {history.length > 0 && (
        <div className="space-y-2 pt-2 border-t border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1">
            <Trophy className="w-4 h-4" />
            Previous Games ({history.length})
          </h3>
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {history.map((pastGame) => (
              <div
                key={pastGame.id}
                className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-800/50 rounded-lg text-sm"
              >
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-1.5 py-0.5 rounded ${
                    pastGame.status === 'completed' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                  }`}>
                    {pastGame.status}
                  </span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {pastGame.winnerNicknames.length > 0 ? pastGame.winnerNicknames.join(', ') : 'No winner'}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                  <Users className="w-3 h-3" />
                  {pastGame.playerCount}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
